import React, { Fragment } from "react";
import {
  ProgressSteps as BaseUIProgressSteps,
  ProgressStepsProps as BaseProgressStepsProps,
  NumberedStep,
  Step,
  ProgressStepsOverrides,
} from "baseui/progress-steps";
import { Overrides } from "baseui/overrides";
import { mergeOverrides } from "baseui";
import { Theme } from "../../../../styling/baseui_theme";
import styled from "styled-components";
import { HiddenAccessibleSpan } from "../../../accessibility/hidden_accessible_span";

export interface ProgressStep {
  title: string;
  content: React.ReactNode;
}

export interface ProgressStepsProps extends BaseProgressStepsProps {
  steps: ProgressStep[];
  current: number;
  setCurrent: (current: number) => void;
  dataTest: string;
  useNumberedSteps?: boolean;
  orientation?: ProgressStepsOrientation;
  styletronOverrides?: ProgressStepsOverrides;
}

export enum ProgressStepsOrientation {
  Horizontal = "Horizontal",
  Vertical = "Vertical",
}

const StyledHorizontalStepperContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 4px;
  padding-inline-start: 4px;
`;

const StyledHorizontalContent = styled.div`
  width: 100%;
`;

const horizontalStepsOverrides: Overrides<
  ProgressStepsOverrides & {
    $isActive: boolean;
    $isCompleted: boolean;
  }
> = {
  Root: {
    style: {
      display: "flex",
      flexDirection: "row",
      width: "100%",
    },
  },
  StepRoot: {
    style: {
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
      alignItems: "center",
      marginBottom: "1em",
    },
  },
  Content: {
    style: {
      marginLeft: "0px",
    },
  },
  Title: {
    style: {
      textAlign: "center",
      paddingTop: "10px",
      paddingBottom: "10px",
    },
  },
  Description: {
    style: {
      position: "absolute",
    },
  },
  Icon: {
    style: {
      zIndex: 2,
    },
  },
  Tail: {
    style: ({ $theme }) => {
      const font = $theme.typography.ParagraphMedium;
      const titlePad = $theme.sizing.scale800;
      return {
        position: "absolute",
        height: $theme.sizing.scale0,
        width: "100%",
        marginTop: `calc((${titlePad} + (${font.lineHeight}) / 2) - 3px)`,
        left: "calc(50.5%)", // takes care of weirdness with non-numbered steps
        right: "calc(-49%)", // takes care of weirdness with non-numbered steps
      };
    },
  },
};

const defaultOverrides: Overrides<
  ProgressStepsOverrides & {
    $isActive: boolean;
    $isCompleted: boolean;
  }
> = {
  Icon: {
    style: ({
      $theme,
      $isActive,
      $isCompleted,
    }: {
      $theme: Theme;
      $isActive: boolean;
      $isCompleted: boolean;
    }) => ({
      backgroundColor:
        $isActive || $isCompleted
          ? $theme.colors.contentPrimary
          : $theme.colors.backgroundSecondary,
      color:
        $isActive || $isCompleted
          ? $theme.colors.contentInversePrimary
          : $theme.colors.contentTertiary,
    }),
  },
  Title: {
    style: ({
      $theme,
      $isActive,
      $isCompleted,
    }: {
      $theme: Theme;
      $isActive: boolean;
      $isCompleted: boolean;
    }) => ({
      color:
        $isActive || $isCompleted ? $theme.colors.contentPrimary : $theme.colors.contentTertiary,
      fontSize: $theme.typography.ParagraphMedium.fontSize,
      fontWeight: $theme.typography.ParagraphMedium.fontWeight,
      lineHeight: $theme.typography.LabelMedium.lineHeight, // needed to align Title with Icon
    }),
  },
  Content: {
    style: ({ $theme }) => ({
      color: $theme.colors.contentPrimary,
    }),
  },
  Tail: {
    style: ({ $theme, $isCompleted }: { $theme: Theme; $isCompleted: boolean }) => ({
      backgroundColor: $isCompleted
        ? $theme.colors.contentPrimary
        : $theme.colors.backgroundSecondary,
    }),
  },
};

const getStepAriaText = (stepTitle: string, stepIndex: number, currentIndex: number) => {
  if (stepIndex < currentIndex) {
    return `${stepTitle}: completed.`;
  } else if (stepIndex === currentIndex) {
    return `${stepTitle}: current step.`;
  }
  return `${stepTitle}: not yet completed.`;
};

export const ProgressSteps = ({
  steps,
  current,
  setCurrent,
  dataTest,
  useNumberedSteps = true,
  orientation = ProgressStepsOrientation.Horizontal,
  styletronOverrides,
}: ProgressStepsProps) => {
  let styleOverrides = defaultOverrides;

  if (orientation === ProgressStepsOrientation.Horizontal) {
    styleOverrides = mergeOverrides(horizontalStepsOverrides, styleOverrides);
  }

  if (styletronOverrides) {
    styleOverrides = mergeOverrides(
      styleOverrides,
      styletronOverrides as Overrides<
        ProgressStepsOverrides & {
          $isActive: boolean;
          $isCompleted: boolean;
        }
      >
    );
  }

  if (current < 0) {
    setCurrent(0);
  } else if (current >= steps?.length) {
    setCurrent(steps ? steps.length - 1 : 0);
  }

  return (
    <div data-test={dataTest}>
      <BaseUIProgressSteps current={current} overrides={styleOverrides}>
        {steps?.map((step, index) => {
          const accessibleTitle = (
            <Fragment>
              <div aria-hidden={true}>{step.title}</div>
              <HiddenAccessibleSpan>
                {getStepAriaText(step.title, index, current)}
              </HiddenAccessibleSpan>
            </Fragment>
          );

          if (useNumberedSteps) {
            return (
              <NumberedStep
                key={step.title}
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore - A React Node is actually allowed here (just like the Step component below)
                title={accessibleTitle}
              >
                {orientation === ProgressStepsOrientation.Vertical && step.content}
              </NumberedStep>
            );
          } else {
            return (
              <Step key={step.title} title={accessibleTitle}>
                {orientation === ProgressStepsOrientation.Vertical && step.content}
              </Step>
            );
          }
        })}
      </BaseUIProgressSteps>
      {orientation === ProgressStepsOrientation.Horizontal && (
        <StyledHorizontalStepperContainer>
          <StyledHorizontalContent>{steps[current].content}</StyledHorizontalContent>
        </StyledHorizontalStepperContainer>
      )}
    </div>
  );
};
